import axios from "axios";
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PhotoIcon,
  LinkIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { IFeedEntry } from "../store/FeedEntry";
import useAutoHideOnScroll from "../hooks/useAutoHideOnScroll";
import "./VibesView.css";

const HOSTNAME = process.env.REACT_APP_LRNER_API_URL || "http://localhost:4000";
const PAGE_SIZE = 10;
const HIDDEN_TAGS = new Set([
  "picfreededphotos",
  "uk-adverts",
  "classic-tv-commercials",
  "television",
  "vintage-advertisements",
  "vintage_advertisements",
  "individual-image-collections",
]);

function readableContent(content: string) {
  if (!content.includes("<")) return content;

  const parsed = new DOMParser().parseFromString(content, "text/html");
  return (parsed.body.textContent || "").replace(/\s+/g, " ").trim();
}

function VibePost({
  entry,
  onTag,
  initialMediaIndex = 0,
}: {
  entry: IFeedEntry;
  onTag: (tag: string) => void;
  initialMediaIndex?: number;
}) {
  const [imageIndex, setImageIndex] = useState(initialMediaIndex);
  const [showAllTags, setShowAllTags] = useState(false);
  const images = entry.images.filter(Boolean);
  const videos = (entry.videos || []).filter(Boolean);
  const media = [
    ...videos.map((src) => ({ src, type: "video" as const })),
    ...images.map((src) => ({ src, type: "image" as const })),
  ];
  const tags = (entry.categories || []).filter((tag) => !HIDDEN_TAGS.has(tag));
  const visibleTags = showAllTags ? tags : tags.slice(0, 10);

  useEffect(() => {
    setImageIndex(media.length > 0 ? Math.min(initialMediaIndex, media.length - 1) : 0);
  }, [entry.id, initialMediaIndex, media.length]);

  const moveImage = (direction: number) => {
    setImageIndex((current) => (current + direction + media.length) % media.length);
  };

  const handleImageError = () => {
    if (media.length > 1) moveImage(1);
  };

  return (
    <article className="vibe-post">
      <header className="vibe-post__header">
        <span>{entry.creation_year || "FOUND IMAGE"}</span>
        <div className="vibe-post__actions">
          <Link
            className="vibe-icon-button"
            to={`/vibes/${entry.id}/media/${imageIndex}`}
            aria-label={`Permalink to ${entry.title}`}
            title="Open permalink"
          >
            <LinkIcon />
          </Link>
          <a
            className="vibe-icon-button"
            href={entry.object_url}
            target="_blank"
            rel="noreferrer"
            aria-label={`View the original source for ${entry.title}`}
            title="View original source"
          >
            <ArrowTopRightOnSquareIcon />
          </a>
        </div>
      </header>

      <div className="vibe-post__image-stage">
        {media[imageIndex]?.type === "video" ? (
          <video
            key={media[imageIndex].src}
            src={media[imageIndex].src}
            controls
            playsInline
            preload="metadata"
            aria-label={entry.title || "Archive video"}
            onError={handleImageError}
          />
        ) : media[imageIndex]?.type === "image" ? (
          <img
            src={media[imageIndex].src}
            alt={entry.title || "Archive photograph"}
            loading="lazy"
            onError={handleImageError}
          />
        ) : (
          <div className="vibe-post__missing-image">
            <PhotoIcon />
            <span>Media unavailable</span>
          </div>
        )}

        {media.length > 1 && (
          <>
            <button
              className="vibe-image-nav vibe-image-nav--previous"
              onClick={() => moveImage(-1)}
              aria-label="Previous media"
            >
              <ChevronLeftIcon />
            </button>
            <button
              className="vibe-image-nav vibe-image-nav--next"
              onClick={() => moveImage(1)}
              aria-label="Next media"
            >
              <ChevronRightIcon />
            </button>
            <span className="vibe-post__image-count">
              {imageIndex + 1}/{media.length}
            </span>
          </>
        )}
      </div>

      <div className="vibe-post__body">
        <h2>{entry.title || "Untitled archive photograph"}</h2>
        {entry.content && <p>{readableContent(entry.content)}</p>}

        <div className="vibe-post__tags" aria-label="Media tags">
          {visibleTags.map((tag) => (
            <button key={tag} onClick={() => onTag(tag)}>
              #{tag}
            </button>
          ))}
          {tags.length > 10 && (
            <button
              className="vibe-post__more-tags"
              onClick={() => setShowAllTags((visible) => !visible)}
            >
              {showAllTags ? "show fewer" : `+${tags.length - 10}`}
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

export default function VibesView() {
  const { entryId, mediaIndex: mediaIndexParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [entries, setEntries] = useState<IFeedEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const entriesRef = useRef<IFeedEntry[]>([]);
  const loadingRef = useRef(false);
  const requestGeneration = useRef(0);
  const loader = useRef<HTMLDivElement>(null);
  const scrollArea = useRef<HTMLElement>(null);
  const mastheadHidden = useAutoHideOnScroll(scrollArea);

  const tag = searchParams.get("tag") || "";
  const feedPermalink = window.location.pathname.endsWith("/feed");
  const focusedPermalink = Boolean(entryId) && !feedPermalink;
  const requestedMediaIndex = Math.max(0, Number.parseInt(mediaIndexParam || "0", 10) || 0);

  const fetchPage = useCallback(
    async (reset: boolean, generation: number) => {
      if (loadingRef.current) return;

      loadingRef.current = true;
      setLoading(true);
      setError(null);

      const currentEntries = reset ? [] : entriesRef.current;

      try {
        const pinnedResponse =
          reset && entryId
            ? await axios.get(`${HOSTNAME}/api/v1/vibes/${entryId}`)
            : null;
        const pinnedEntry: IFeedEntry | undefined = pinnedResponse?.data?.data;

        if (focusedPermalink) {
          const nextEntries = pinnedEntry ? [pinnedEntry] : [];
          entriesRef.current = nextEntries;
          setEntries(nextEntries);
          setHasMore(false);
          return;
        }

        const exclusions = [
          ...(pinnedEntry ? [pinnedEntry.id] : []),
          ...currentEntries.map((entry) => entry.id),
        ];
        const response = await axios.get(`${HOSTNAME}/api/v1/vibes`, {
          params: {
            tag: tag || undefined,
            limit: PAGE_SIZE,
            exclude: exclusions.length > 0 ? exclusions.join(",") : undefined,
          },
        });

        if (generation !== requestGeneration.current) return;

        const incoming: IFeedEntry[] = response.data?.data || [];
        const knownIds = new Set(currentEntries.map((entry) => entry.id));
        const uniqueIncoming = incoming.filter((entry) => !knownIds.has(entry.id));
        const nextEntries = reset
          ? [...(pinnedEntry ? [pinnedEntry] : []), ...uniqueIncoming]
          : [...currentEntries, ...uniqueIncoming];

        entriesRef.current = nextEntries;
        setEntries(nextEntries);
        setHasMore(uniqueIncoming.length === PAGE_SIZE);
      } catch (_requestError) {
        if (generation === requestGeneration.current) {
          setError("The archive feed could not be reached. Try again in a moment.");
        }
      } finally {
        if (generation === requestGeneration.current) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    },
    [entryId, focusedPermalink, tag]
  );

  const resetFeed = useCallback(() => {
    requestGeneration.current += 1;
    const generation = requestGeneration.current;
    loadingRef.current = false;
    entriesRef.current = [];
    setEntries([]);
    setHasMore(true);
    scrollArea.current?.scrollTo({ top: 0, behavior: "smooth" });
    void fetchPage(true, generation);
  }, [fetchPage]);

  useEffect(() => {
    resetFeed();
  }, [resetFeed]);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingRef.current) return;
    void fetchPage(false, requestGeneration.current);
  }, [fetchPage, hasMore]);

  useEffect(() => {
    const target = loader.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      { root: scrollArea.current, rootMargin: "700px 0px" }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [loadMore]);

  const chooseTag = (nextTag: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("tag", nextTag);
    next.delete("collection");
    setSearchParams(next);
  };

  const clearTag = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("tag");
    setSearchParams(next);
  };

  return (
    <main className="vibes" ref={scrollArea}>
      <header className={`vibes__masthead${mastheadHidden ? " is-hidden" : ""}`}>
        <div className="vibes__masthead-copy">
          <h1>VIBES</h1>
          <p>vintage pictures + moving images</p>
        </div>
        {focusedPermalink ? (
          <Link className="vibes__shuffle" to={`/vibes/${entryId}/media/${requestedMediaIndex}/feed`}>
            Continue feed
          </Link>
        ) : (
          <button className="vibes__shuffle" onClick={resetFeed}>
            <ArrowPathIcon />
            New mix
          </button>
        )}
      </header>

      <div className="vibes__filter">
        {focusedPermalink ? (
          <span>permalink</span>
        ) : feedPermalink ? (
          <span>permalink + random feed</span>
        ) : tag ? (
          <button onClick={clearTag}>
            #{tag}
            <XMarkIcon />
          </button>
        ) : (
          <span>random feed</span>
        )}
      </div>

      <section className="vibes__feed" aria-live="polite">
        {entries.map((entry, index) => (
          <VibePost
            key={entry.id}
            entry={entry}
            onTag={chooseTag}
            initialMediaIndex={index === 0 && entryId ? requestedMediaIndex : 0}
          />
        ))}

        {loading && (
          <div className="vibes__status">
            <span className="vibes__loader" />
            finding media…
          </div>
        )}

        {error && (
          <div className="vibes__empty">
            <p>{error}</p>
            <button onClick={resetFeed}>Try again</button>
          </div>
        )}

        {!loading && !error && entries.length === 0 && (
          <div className="vibes__empty">
            <PhotoIcon />
            <h2>No media for this tag yet.</h2>
            <p>Try another trail through the feed.</p>
            {tag && <button onClick={clearTag}>Clear #{tag}</button>}
          </div>
        )}

        {!loading && entries.length > 0 && !hasMore && (
          <div className="vibes__status">
            End of this trail. <button onClick={resetFeed}>Reshuffle it.</button>
          </div>
        )}

        <div className="vibes__sentinel" ref={loader} />
      </section>
    </main>
  );
}
